import React, { useState, useEffect, useCallback } from 'react';
import supabase from '../../supabase.js';
import AdminBar from './adminBar.jsx';
import { PDFDocument } from 'pdf-lib';
import { Helmet } from 'react-helmet-async';

export default function InvitedReviews() {
    const [currentReview, setCurrentReview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState('');
    const [requestsLeft, setRequestsLeft] = useState(0);
    const [isActionLoading, setIsActionLoading] = useState(false);
    const [signedUrl, setSignedUrl] = useState('');
    const [pendingAction, setPendingAction] = useState(null);
    
    const [pdfMetadata, setPdfMetadata] = useState(null);
    const [isPDF, setIsPDF] = useState(false);
    const [ipHistoryCount, setIpHistoryCount] = useState(0);

    const analyzePDF = async (url) => {
        try {
            const existingPdfBytes = await fetch(url).then(res => res.arrayBuffer());
            const pdfDoc = await PDFDocument.load(existingPdfBytes, { updateMetadata: false });
            
            const producer = (pdfDoc.getProducer() || "Unknown").toLowerCase();
            const creator = (pdfDoc.getCreator() || "Unknown").toLowerCase();
            const creationDate = pdfDoc.getCreationDate();
            
            const forgeryTools = ['photoshop', 'illustrator', 'canva', 'photopea', 'inkscape', 'coreldraw', 'gimp', 'adobe acrobat'];
            const editors = ['ilovepdf', 'smallpdf', 'sodapdf', 'pdf2go', 'sejda', 'pdfescape'];

            let riskLevel = 'clean';
            let reason = '';

            if (forgeryTools.some(t => producer.includes(t) || creator.includes(t))) {
                riskLevel = 'high';
                reason = "FORGERY TOOL DETECTED";
            } else if (editors.some(t => producer.includes(t) || creator.includes(t))) {
                riskLevel = 'high';
                reason = "ONLINE PDF EDITOR USED";
            }

            setPdfMetadata({ 
                producer, 
                creator, 
                creationDate: creationDate?.toString() || "Unknown",
                riskLevel,
                reason
            });
        } catch (err) {
            setPdfMetadata({ producer: "Read Error", riskLevel: 'high', reason: "ENCRYPTED/CORRUPT" });
        }
    };

    // 1. UPDATED: Fetch count of reviews that are NOT approved AND NOT deleted
    const fetchPendingCount = useCallback(async () => {
        const { count } = await supabase
            .from('reviews')
            .select('*', { count: 'exact', head: true })
            .eq('is_approved', false)
            .eq('submission_type', 'invited')
            .eq('is_deleted', false); 
        setRequestsLeft(count || 0);
    }, []);

    // 2. UPDATED: Fetch the next review that is NOT approved AND NOT deleted
    const fetchNextPendingReview = useCallback(async () => {
        setLoading(true); setErrorMsg(''); setSignedUrl(''); setPendingAction(null); setPdfMetadata(null); setIpHistoryCount(0);
        try {
            const { data, error } = await supabase
                .from('reviews')
                .select('*')
                .eq('is_approved', false)
                .eq('is_deleted', false) 
                .eq('submission_type', 'invited') 
                .order('id', { ascending: true })
                .limit(1);
            
            if (error) throw error;
            
            if (data?.[0]) {
                const review = data[0];
                setCurrentReview(review);
                const isDocPDF = review.screenshot_url?.toLowerCase().endsWith('.pdf');
                setIsPDF(isDocPDF);

                const { count: histCount } = await supabase
                    .from('reviews')
                    .select('*', { count: 'exact', head: true })
                    .eq('ip_address', review.ip_address);
                setIpHistoryCount(histCount || 0);

                const { data: sData } = await supabase.storage.from('review-proof').createSignedUrl(review.screenshot_url, 600);
                setSignedUrl(sData?.signedUrl);

                if (isDocPDF && sData?.signedUrl) {
                    await analyzePDF(sData.signedUrl);
                }
            } else {
                setCurrentReview(null);
                setErrorMsg("No pending audits.");
            }
        } catch (err) { setErrorMsg(err.message); } finally { setLoading(false); }
    }, []);

    // 3. UPDATED: Handle Decline by setting is_deleted: true
    const handleAction = async () => {
        if (!currentReview || !pendingAction || isActionLoading) return;
        setIsActionLoading(true);
        
        try {
            const isApprove = pendingAction === 'approve';
            const targetCompanyWww = currentReview.company;

            // 1. UPDATE THE REVIEW STATUS
            const { error: reviewError } = await supabase
                .from('reviews')
                .update({ 
                    is_approved: isApprove, 
                    is_deleted: !isApprove, 
                    status: isApprove ? 'verified' : 'declined' 
                })
                .eq('id', currentReview.id);

            if (reviewError) throw reviewError;

            // 2. TRIGGER RECALCULATION IF APPROVED
            // We do this even on decline just to be safe, but it's mandatory on Approval
            if (isApprove) {
                // Fetch all approved reviews for this company to get the NEW average
                const { data: allApprovedReviews, error: fetchError } = await supabase
                    .from('reviews')
                    .select('stars')
                    .eq('company', targetCompanyWww)
                    .eq('is_approved', true)
                    .eq('is_deleted', false);

                if (fetchError) throw fetchError;

                if (allApprovedReviews && allApprovedReviews.length > 0) {
                    const totalStars = allApprovedReviews.reduce((acc, r) => acc + r.stars, 0);
                    const newTrustScore = parseFloat((totalStars / allApprovedReviews.length).toFixed(1));
                    const newReviewCount = allApprovedReviews.length;

                    const { error: bizUpdateError } = await supabase
                        .from('businesses')
                        .update({ 
                            trustscore: newTrustScore, 
                            reviews: newReviewCount 
                        })
                        .eq('www', targetCompanyWww);

                    if (bizUpdateError) throw bizUpdateError;
                    
                    console.log(`Updated ${targetCompanyWww}: Score ${newTrustScore}, Count ${newReviewCount}`);
                } else {
                    // If for some reason there are no reviews left (e.g., you deleted the last one)
                    await supabase
                        .from('businesses')
                        .update({ trustscore: 0, reviews: 0 })
                        .eq('www', targetCompanyWww);
                }
            }

            // 3. UI CLEANUP & MOVE TO NEXT
            await fetchPendingCount(); 
            await fetchNextPendingReview(); 
            
        } catch (err) { 
            console.error("CRITICAL SYNC ERROR:", err);
            alert("Action failed: " + err.message); 
        } finally { 
            setIsActionLoading(false); 
        }
    };
    useEffect(() => { fetchPendingCount(); fetchNextPendingReview(); }, []);

    if (loading) return <div className='p-20 text-center font-black animate-pulse text-white bg-[#0f172a] min-h-screen uppercase'>Loading Intelligence...</div>;

    return (
        <div className='w-full min-h-screen bg-[#0f172a] flex flex-col items-center pb-20 font-poppins text-white'>
                   <Helmet>
                    <title>Admin | Sitemanifest</title> 
                    
                    <meta name="robots" content="noindex, nofollow" />
                    
                    <meta name="googlebot" content="noarchive" />
                  </Helmet>
            <AdminBar />
            
            <div className='pt-24 pb-10 text-center'>
                <h1 className='text-5xl font-black tracking-tighter uppercase italic'>Evidence Terminal</h1>
                <p className='text-blue-400 font-bold mt-2 tracking-widest'>{requestsLeft} REVIEWS IN QUEUE</p>
            </div>

            {!currentReview ? (
                <div className='bg-slate-800 p-12 rounded-[3rem] text-center border border-slate-700'>
                    <p className='text-slate-400 font-bold mb-6'>{errorMsg || "Vault Clear."}</p>
                    <button onClick={() => fetchNextPendingReview()} className='bg-blue-600 text-white px-10 py-4 rounded-2xl font-black'>REFRESH</button>
                </div>
            ) : (
                <div className='flex flex-col lg:flex-row gap-10 px-10 max-w-7xl w-full'>
                    {/* LEFT SIDE: PREVIEW */}
                    <div className='flex-1 flex flex-col gap-6'>
                        <div className={`aspect-[4/3] bg-black rounded-[3rem] flex items-center justify-center overflow-hidden shadow-2xl border-[12px] transition-all duration-500 ${ (currentReview.ai_score > 0.6 || currentReview.is_flagged || pdfMetadata?.riskLevel === 'high' || currentReview.ip_fraud_score > 75 || ipHistoryCount > 2) ? 'border-red-600 shadow-red-900/40' : 'border-slate-800'}`}>
                            {isPDF ? (
                                <iframe src={`${signedUrl}#toolbar=0`} className='w-full h-full border-none' title="PDF Preview" />
                            ) : (
                                <img src={signedUrl} alt="Proof" className='w-full h-full object-contain' />
                            )}
                        </div>
                        
                        <div className='bg-slate-800/80 border border-slate-700 p-8 rounded-[3rem] shadow-xl'>
                            <h2 className='text-blue-400 font-black text-xs uppercase tracking-[0.2em] mb-4'>Testimonial Content</h2>
                            <p className='text-xl font-medium leading-relaxed italic text-slate-200'>
                                "{currentReview.text || "No review text provided."}"
                            </p>
                            <div className='mt-6 flex gap-6 border-t border-slate-700 pt-6'>
                                <DetailItem label="Author" value={currentReview.author} />
                                <DetailItem label="Target Company" value={currentReview.company} />
                                <DetailItem label="Rating" value={`${currentReview.stars} / 5`} />
                            </div>
                        </div>

                        <div className='flex gap-4'>
                            <button onClick={() => setPendingAction('decline')} className={`flex-1 py-5 rounded-3xl font-black transition-all ${pendingAction === 'decline' ? 'bg-red-600 ring-8 ring-red-900/50 scale-95' : 'bg-slate-800 border border-slate-700 opacity-50'}`}>DECLINE</button>
                            <button onClick={() => setPendingAction('approve')} className={`flex-1 py-5 rounded-3xl font-black transition-all ${pendingAction === 'approve' ? 'bg-green-600 ring-8 ring-green-900/50 scale-95' : 'bg-slate-800 border border-slate-700 opacity-50'}`}>APPROVE</button>
                        </div>
                        <button onClick={handleAction} disabled={!pendingAction || isActionLoading} className='w-full py-8 bg-white text-black font-black text-2xl rounded-[2rem] shadow-2xl disabled:opacity-20 transition-all uppercase'>
                            {isActionLoading ? 'Executing...' : 'Commit Decision'}
                        </button>
                    </div>

                    {/* RIGHT SIDE: METADATA */}
                    <div className='w-full lg:w-[450px] flex flex-col gap-6'>
                        <div className={`p-8 rounded-[3rem] shadow-2xl border transition-all duration-700 
                            ${(currentReview.ai_score > 0.6 || currentReview.is_flagged || pdfMetadata?.riskLevel === 'high' || ipHistoryCount > 3) ? 'bg-red-600 animate-pulse border-transparent' : 
                              (currentReview.ai_score > 0.3 || currentReview.ip_fraud_score > 40 || ipHistoryCount > 1) ? 'bg-orange-500 border-transparent' : 
                              'bg-slate-800 border-slate-700'}`}>
                            
                            <h3 className='font-black text-[10px] uppercase tracking-[0.2em] mb-4 text-white/60'>File Integrity Audit</h3>
                            <p className='text-3xl font-black italic uppercase text-white'>
                                {isPDF ? (pdfMetadata?.reason || "Verified Origin") : 
                                 (currentReview.flag_reason ? currentReview.flag_reason : 
                                  currentReview.ai_score > 0.8 ? "🚨 AI GENERATED" : "Low Risk Pattern")}
                            </p>

                            <div className="mt-6 text-[11px] font-mono space-y-2 bg-black/30 p-4 rounded-2xl border border-white/10 text-white">
                                {isPDF ? (
                                    <>
                                        <p><span className="opacity-50">PRODUCER:</span> {pdfMetadata?.producer || 'N/A'}</p>
                                        <p><span className="opacity-50">CREATOR:</span> {pdfMetadata?.creator || 'N/A'}</p>
                                        <p><span className="opacity-50">RISK:</span> {pdfMetadata?.riskLevel?.toUpperCase()}</p>
                                    </>
                                ) : (
                                    <>
                                        <p><span className="opacity-50">AI FRAUD RISK:</span> 
                                            <span className={`ml-2 font-bold ${currentReview.ai_score > 0.6 ? 'text-yellow-300' : 'text-white'}`}>
                                                {(currentReview.ai_score * 100).toFixed(1)}%
                                            </span>
                                        </p>
                                        <p><span className="opacity-50">INTEGRITY:</span> {currentReview.is_flagged ? "MANIPULATED" : "PASS"}</p>
                                        <p><span className="opacity-50">IP REPUTATION:</span> {ipHistoryCount > 2 ? "SUSPICIOUS (MULTIPLE)" : "UNIQUE"}</p>
                                    </>
                                )}
                            </div>
                        </div>

                        <div className={`p-8 rounded-[3rem] shadow-2xl border transition-all 
                            ${(currentReview.ip_fraud_score > 50 || ipHistoryCount > 2) ? 'bg-red-900/20 border-red-500' : 'bg-slate-800 border-slate-700'}`}>
                            <h3 className='font-black text-xs uppercase tracking-[0.2em] mb-4 text-blue-400'>Network Intelligence</h3>
                            <div className='flex items-end gap-4'>
                                <div className='flex-1'>
                                    <p className='text-[10px] font-black text-slate-500 uppercase mb-1'>Origin IP</p>
                                    <p className='font-mono text-lg font-bold'>{currentReview.ip_address || 'UNKNOWN'}</p>
                                    <p className={`text-[10px] font-bold mt-1 uppercase ${ipHistoryCount > 1 ? 'text-yellow-500' : 'text-slate-500'}`}>
                                        History: {ipHistoryCount} Submissions
                                    </p>
                                </div>
                                <div className='text-right'>
                                    <p className='text-[10px] font-black text-slate-500 uppercase mb-1'>Fraud Score</p>
                                    <p className={`text-4xl font-black ${currentReview.ip_fraud_score > 75 ? 'text-red-500' : currentReview.ip_fraud_score > 30 ? 'text-orange-400' : 'text-green-500'}`}>
                                        {currentReview.ip_fraud_score ?? '??'}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className='bg-slate-800 border border-slate-700 p-8 rounded-[3rem] shadow-2xl'>
                            <h2 className='text-purple-400 font-black text-xs uppercase tracking-[0.2em] mb-4'>Device Signature</h2>
                            <div className='text-[11px] font-mono text-slate-400 break-all bg-black/20 p-4 rounded-xl border border-white/5'>
                                {currentReview.user_agent || "No Agent Captured"}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function DetailItem({ label, value }) {
    return (
        <div className="flex-1">
            <p className='text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1'>{label}</p>
            <p className='font-bold text-sm text-white uppercase truncate'>{value || '---'}</p>
        </div>
    );
}